/**
 * Navigator runtime: state initialisation, action dispatch and bubbling, focus
 * and beforeRemove events, and per-route navigation objects.
 *
 * Ported from @react-navigation/core 6.4.9 (useNavigationBuilder and the hooks
 * it composes). Screens keep using the same navigation API (navigate, replace,
 * push, goBack, canGoBack, addListener('focus' | 'blur' | 'beforeRemove'),
 * setOptions, ...), and the order in which events fire is preserved: several
 * screens rely on it (useTableRefetch refetches on focus, the unsaved-changes
 * guard listens to beforeRemove).
 */
import * as React from 'react'
import {
  Action,
  CommonActions,
  NavigationState,
  PartialState,
  Route,
  Router,
  RouterConfigOptions,
  createKey
} from './routers'

type AnyState = NavigationState | PartialState

// ---------------------------------------------------------------------------
// Contexts
// ---------------------------------------------------------------------------

export interface NavigationStateContextValue {
  isDefault?: true
  state?: AnyState
  getState: () => AnyState | undefined
  setState: (state: AnyState | undefined) => void
  getKey: () => string | undefined
  setKey: (key: string) => void
  getIsInitial: () => boolean
}

const MISSING_CONTEXT_ERROR =
  "Couldn't find a navigation context. Have you wrapped your app with 'NavigationContainer'?"

export const NavigationStateContext = React.createContext<NavigationStateContextValue>(
  {
    isDefault: true,
    get getKey(): any {
      throw new Error(MISSING_CONTEXT_ERROR)
    },
    get setKey(): any {
      throw new Error(MISSING_CONTEXT_ERROR)
    },
    get getState(): any {
      throw new Error(MISSING_CONTEXT_ERROR)
    },
    get setState(): any {
      throw new Error(MISSING_CONTEXT_ERROR)
    },
    get getIsInitial(): any {
      throw new Error(MISSING_CONTEXT_ERROR)
    }
  }
)

export interface ScheduleUpdateContextValue {
  scheduleUpdate: (callback: () => void) => void
  flushUpdates: () => void
}

export const ScheduleUpdateContext = React.createContext<ScheduleUpdateContextValue>(
  {
    scheduleUpdate() {
      throw new Error(MISSING_CONTEXT_ERROR)
    },
    flushUpdates() {
      throw new Error(MISSING_CONTEXT_ERROR)
    }
  }
)

type ListenerMap = {
  action: ChildActionListener
  focus: FocusedNavigationListener
}
type KeyedListenerMap = {
  getState: () => AnyState | undefined
  beforeRemove: (action: Action) => boolean
}

export type ChildActionListener = (
  action: Action,
  visitedNavigators?: Set<string>
) => boolean
export type FocusedNavigationCallback<T> = (navigation: NavigationHelpers) => T
export type FocusedNavigationListener = <T>(
  callback: FocusedNavigationCallback<T>
) => { handled: boolean; result: T }

export interface NavigationBuilderContextValue {
  onAction?: (action: Action, visitedNavigators?: Set<string>) => boolean
  addListener?: <T extends keyof ListenerMap>(
    type: T,
    listener: ListenerMap[T]
  ) => () => void
  addKeyedListener?: <T extends keyof KeyedListenerMap>(
    type: T,
    key: string,
    listener: KeyedListenerMap[T]
  ) => () => void
  onRouteFocus?: (key: string) => void
  onUnhandledAction?: (action: Action) => void
}

export const NavigationBuilderContext = React.createContext<NavigationBuilderContextValue>(
  {}
)

export const NavigationContext = React.createContext<
  NavigationProp | undefined
>(undefined)
export const NavigationRouteContext = React.createContext<Route | undefined>(
  undefined
)
export const NavigationHelpersContext = React.createContext<
  NavigationHelpers | undefined
>(undefined)

// Root-level navigation object (used when a component is outside any screen).
export const NavigationContainerRefContext = React.createContext<any>(undefined)

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EventArg {
  readonly type: string
  readonly target?: string
  readonly data?: any
  /** Only on events emitted with `canPreventDefault` (e.g. beforeRemove). */
  readonly defaultPrevented: boolean
  preventDefault: () => void
}

type Listener = (e: EventArg) => void

export interface NavigationHelpers {
  dispatch: (action: Action | ((state: NavigationState) => Action)) => void
  emit: (event: {
    type: string
    data?: any
    target?: string
    canPreventDefault?: boolean
  }) => EventArg
  isFocused: () => boolean
  canGoBack: () => boolean
  getId: () => string | undefined
  getParent: (id?: string) => any
  getState: () => NavigationState
  [helper: string]: any
}

export interface NavigationProp extends NavigationHelpers {
  addListener: (type: string, callback: Listener) => () => void
  removeListener: (type: string, callback: Listener) => void
  setOptions: (options: object) => void
  setParams: (params: object) => void
  navigate: (...args: any[]) => void
  goBack: () => void
}

export interface Descriptor {
  route: Route
  navigation: NavigationProp
  options: Record<string, any>
  render: () => React.ReactElement
}

export interface ScreenProps {
  name: string
  component?: React.ComponentType<any>
  options?: object | ((props: { route: Route; navigation: any }) => object)
  initialParams?: object
  getId?: (options: { params?: any }) => string | undefined
  navigationKey?: string
  children?: (props: { route: Route; navigation: any }) => React.ReactNode
}

// ---------------------------------------------------------------------------
// Screen / Group config elements
// ---------------------------------------------------------------------------

/** Configuration-only element: read by the navigator, renders nothing. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function Screen(_: ScreenProps): null {
  return null
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function Group(_: {
  navigationKey?: string
  screenOptions?: object
  children?: React.ReactNode
}): null {
  return null
}

interface ScreenConfigWithParent {
  keys: (string | undefined)[]
  options?: any[]
  props: ScreenProps
}

const isValidKey = (key: unknown) =>
  key === undefined || (typeof key === 'string' && key !== '')

const getRouteConfigsFromChildren = (
  children: React.ReactNode,
  groupKey?: string,
  groupOptions?: any[]
): ScreenConfigWithParent[] =>
  React.Children.toArray(children).reduce<ScreenConfigWithParent[]>(
    (acc, child) => {
      if (React.isValidElement(child)) {
        const props: any = child.props
        if (child.type === Screen) {
          if (!isValidKey(props.navigationKey)) {
            throw new Error(
              `Got an invalid 'navigationKey' prop (${JSON.stringify(
                props.navigationKey
              )}) for the screen '${props.name}'.`
            )
          }
          acc.push({
            keys: [groupKey, props.navigationKey],
            options: groupOptions,
            props
          })
          return acc
        }

        if (child.type === React.Fragment || child.type === Group) {
          if (!isValidKey(props.navigationKey)) {
            throw new Error(
              `Got an invalid 'navigationKey' prop (${JSON.stringify(
                props.navigationKey
              )}) for the group.`
            )
          }
          acc.push(
            ...getRouteConfigsFromChildren(
              props.children,
              props.navigationKey,
              child.type !== Group
                ? groupOptions
                : groupOptions != null
                ? [...groupOptions, props.screenOptions]
                : [props.screenOptions]
            )
          )
          return acc
        }
      }

      throw new Error(
        `A navigator can only contain 'Screen', 'Group' or 'React.Fragment' as its direct children (found ${
          React.isValidElement(child)
            ? `'${
                typeof child.type === 'string'
                  ? child.type
                  : (child.type as any)?.name
              }'${
                (child.props as any)?.name
                  ? ` for the screen '${(child.props as any).name}'`
                  : ''
              }`
            : typeof child === 'object'
            ? JSON.stringify(child)
            : `'${String(child)}'`
        }).`
      )
    },
    []
  )

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const isArrayEqual = (a: any[], b: any[]) =>
  a.length === b.length && a.every((it, index) => it === b[index])

const isRecordEqual = (a: Record<string, any>, b: Record<string, any>) => {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  return aKeys.length === bKeys.length && aKeys.every(key => a[key] === b[key])
}

/** Keeps its children from re-rendering unless one of its other props changed. */
const StaticContainer = React.memo(
  function StaticContainer(props: any) {
    return props.children
  },
  (prevProps: any, nextProps: any) => {
    const prevPropKeys = Object.keys(prevProps)
    const nextPropKeys = Object.keys(nextProps)

    if (prevPropKeys.length !== nextPropKeys.length) {
      return false
    }

    for (const key of prevPropKeys) {
      if (key === 'children') {
        continue
      }

      if (prevProps[key] !== nextProps[key]) {
        return false
      }
    }

    return true
  }
)

// ---------------------------------------------------------------------------
// Event emitter
// ---------------------------------------------------------------------------

export interface NavigationEventEmitter {
  create: (
    target: string
  ) => {
    addListener: (type: string, callback: Listener) => () => void
    removeListener: (type: string, callback: Listener) => void
  }
  emit: NavigationHelpers['emit']
}

export function useEventEmitter(
  listen?: (e: EventArg) => void
): NavigationEventEmitter {
  const listenRef = React.useRef(listen)

  React.useEffect(() => {
    listenRef.current = listen
  })

  const listeners = React.useRef<Record<string, Record<string, Listener[]>>>(
    Object.create(null)
  )

  const create = React.useCallback((target: string) => {
    const removeListener = (type: string, callback: Listener) => {
      const callbacks = listeners.current[type]
        ? listeners.current[type][target]
        : undefined

      if (!callbacks) {
        return
      }

      const index = callbacks.indexOf(callback)

      if (index > -1) {
        callbacks.splice(index, 1)
      }
    }

    const addListener = (type: string, callback: Listener) => {
      listeners.current[type] = listeners.current[type] || {}
      listeners.current[type][target] = listeners.current[type][target] || []
      listeners.current[type][target].push(callback)

      let removed = false
      return () => {
        if (!removed) {
          removed = true
          removeListener(type, callback)
        }
      }
    }

    return { addListener, removeListener }
  }, [])

  const emit = React.useCallback(
    ({
      type,
      data,
      target,
      canPreventDefault
    }: {
      type: string
      data?: any
      target?: string
      canPreventDefault?: boolean
    }) => {
      const items = listeners.current[type] || {}

      const callbacks =
        target !== undefined
          ? items[target]?.slice()
          : ([] as Listener[])
              .concat(...Object.keys(items).map(t => items[t]))
              .filter((cb, i, self) => self.lastIndexOf(cb) === i)

      const event: any = {
        get type() {
          return type
        }
      }

      if (target !== undefined) {
        Object.defineProperty(event, 'target', {
          enumerable: true,
          get() {
            return target
          }
        })
      }

      if (data !== undefined) {
        Object.defineProperty(event, 'data', {
          enumerable: true,
          get() {
            return data
          }
        })
      }

      if (canPreventDefault) {
        let defaultPrevented = false

        Object.defineProperties(event, {
          defaultPrevented: {
            enumerable: true,
            get() {
              return defaultPrevented
            }
          },
          preventDefault: {
            enumerable: true,
            value() {
              defaultPrevented = true
            }
          }
        })
      }

      listenRef.current?.(event)

      callbacks?.forEach(cb => cb(event))

      return event as EventArg
    },
    []
  )

  return React.useMemo(() => ({ create, emit }), [create, emit])
}

// ---------------------------------------------------------------------------
// Child listeners
// ---------------------------------------------------------------------------

export function useChildListeners() {
  const { current: listeners } = React.useRef<{
    action: ChildActionListener[]
    focus: FocusedNavigationListener[]
  }>({
    action: [],
    focus: []
  })

  const addListener = React.useCallback(
    <T extends keyof ListenerMap>(type: T, listener: ListenerMap[T]) => {
      ;(listeners[type] as any[]).push(listener)

      let removed = false
      return () => {
        const index = (listeners[type] as any[]).indexOf(listener)

        if (!removed && index > -1) {
          removed = true
          ;(listeners[type] as any[]).splice(index, 1)
        }
      }
    },
    [listeners]
  )

  return { listeners, addListener }
}

export function useKeyedChildListeners() {
  const { current: keyedListeners } = React.useRef<{
    getState: Record<string, KeyedListenerMap['getState'] | undefined>
    beforeRemove: Record<string, KeyedListenerMap['beforeRemove'] | undefined>
  }>(
    Object.assign(Object.create(null), {
      getState: {},
      beforeRemove: {}
    })
  )

  const addKeyedListener = React.useCallback(
    <T extends keyof KeyedListenerMap>(
      type: T,
      key: string,
      listener: KeyedListenerMap[T]
    ) => {
      ;(keyedListeners[type] as any)[key] = listener

      return () => {
        ;(keyedListeners[type] as any)[key] = undefined
      }
    },
    [keyedListeners]
  )

  return { keyedListeners, addKeyedListener }
}

// ---------------------------------------------------------------------------
// beforeRemove
// ---------------------------------------------------------------------------

const VISITED_ROUTE_KEYS = Symbol('VISITED_ROUTE_KEYS')

export const shouldPreventRemove = (
  emitter: NavigationEventEmitter,
  beforeRemoveListeners: Record<
    string,
    ((action: Action) => boolean) | undefined
  >,
  currentRoutes: { key: string }[],
  nextRoutes: { key?: string }[],
  action: Action
) => {
  const nextRouteKeys = nextRoutes.map(route => route.key)

  // Call these in reverse order so last screens handle the event first
  const removedRoutes = currentRoutes
    .filter(route => !nextRouteKeys.includes(route.key))
    .reverse()

  const visitedRouteKeys: Set<string> =
    (action as any)[VISITED_ROUTE_KEYS] ?? new Set<string>()

  const beforeRemoveAction = {
    ...action,
    [VISITED_ROUTE_KEYS]: visitedRouteKeys
  }

  for (const route of removedRoutes) {
    if (visitedRouteKeys.has(route.key)) {
      continue
    }

    // First, we need to check if any child screens want to prevent it
    const isPrevented = beforeRemoveListeners[route.key]?.(beforeRemoveAction)

    if (isPrevented) {
      return true
    }

    visitedRouteKeys.add(route.key)

    const event = emitter.emit({
      type: 'beforeRemove',
      target: route.key,
      data: { action: beforeRemoveAction },
      canPreventDefault: true
    })

    if (event.defaultPrevented) {
      return true
    }
  }

  return false
}

// ---------------------------------------------------------------------------
// useNavigationBuilder
// ---------------------------------------------------------------------------

export interface NavigatorOptions {
  id?: string
  children: React.ReactNode
  screenOptions?:
    | object
    | ((props: { route: Route; navigation: any }) => object)
  defaultScreenOptions?: object
  [key: string]: any
}

export function useNavigationBuilder(
  createRouter: (options: any) => Router,
  options: NavigatorOptions
) {
  const navigatorKey = React.useRef(`${createKey()}`).current

  const route = React.useContext(NavigationRouteContext) as
    | (Route & { params?: any })
    | undefined

  const { children, ...rest } = options
  const { current: router } = React.useRef<Router>(
    createRouter({
      ...rest,
      ...(route?.params &&
      route.params.state == null &&
      route.params.initial !== false &&
      typeof route.params.screen === 'string'
        ? { initialRouteName: route.params.screen }
        : null)
    })
  )

  const routeConfigs = getRouteConfigsFromChildren(children)

  const screens = routeConfigs.reduce<Record<string, ScreenConfigWithParent>>(
    (acc, config) => {
      if (config.props.name in acc) {
        throw new Error(
          `A navigator cannot contain multiple 'Screen' components with the same name (found duplicate screen named '${config.props.name}')`
        )
      }

      acc[config.props.name] = config
      return acc
    },
    {}
  )

  const routeNames = routeConfigs.map(config => config.props.name)
  const routeKeyList = routeNames.reduce<Record<string, string>>(
    (acc, curr) => {
      acc[curr] = screens[curr].keys.map(key => key ?? '').join(':')
      return acc
    },
    {}
  )
  const routeParamList = routeNames.reduce<Record<string, object | undefined>>(
    (acc, curr) => {
      acc[curr] = screens[curr].props.initialParams
      return acc
    },
    {}
  )
  const routeGetIdList = routeNames.reduce<
    RouterConfigOptions['routeGetIdList']
  >(
    (acc, curr) => Object.assign(acc, { [curr]: screens[curr].props.getId }),
    {}
  )

  if (!routeNames.length) {
    throw new Error(
      "Couldn't find any screens for the navigator. Have you defined any screens as its children?"
    )
  }

  const isStateValid = React.useCallback(
    (state: AnyState) => state.type === undefined || state.type === router.type,
    [router.type]
  )

  const isStateInitialized = React.useCallback(
    (state: AnyState | undefined): state is NavigationState =>
      state !== undefined && state.stale === false && isStateValid(state),
    [isStateValid]
  )

  const {
    state: currentState,
    getState: getCurrentState,
    setState: setCurrentState,
    setKey,
    getKey,
    getIsInitial
  } = React.useContext(NavigationStateContext)

  const stateCleanedUp = React.useRef(false)

  const cleanUpState = React.useCallback(() => {
    setCurrentState(undefined)
    stateCleanedUp.current = true
  }, [setCurrentState])

  const setState = React.useCallback(
    (state: AnyState | undefined) => {
      if (stateCleanedUp.current) {
        // State might have been already cleaned up due to unmount
        // We do not want to expose API allowing to override this
        // This would lead to old data preservation on main navigator unmount
        return
      }
      setCurrentState(state)
    },
    [setCurrentState]
  )

  const [initializedState, isFirstStateInitialization] = React.useMemo(() => {
    const initialRouteParamList = routeNames.reduce<
      Record<string, object | undefined>
    >((acc, curr) => {
      const { initialParams } = screens[curr].props
      const initialParamsFromParams =
        route?.params?.state == null &&
        route?.params?.initial !== false &&
        route?.params?.screen === curr
          ? route.params.params
          : undefined

      acc[curr] =
        initialParams !== undefined || initialParamsFromParams !== undefined
          ? { ...initialParams, ...initialParamsFromParams }
          : undefined

      return acc
    }, {})

    // If the current state isn't initialized on first render, we initialize it
    // We also need to re-initialize it if the state passed from parent was changed (maybe due to reset)
    // Otherwise assume that the state was provided as initial state
    // So we need to rehydrate it to make it usable
    if (
      (currentState === undefined || !isStateValid(currentState)) &&
      route?.params?.state == null
    ) {
      return [
        router.getInitialState({
          routeNames,
          routeParamList: initialRouteParamList,
          routeGetIdList
        }),
        true
      ] as const
    } else {
      return [
        router.getRehydratedState(
          route?.params?.state ?? (currentState as PartialState),
          {
            routeNames,
            routeParamList: initialRouteParamList,
            routeGetIdList
          }
        ),
        false
      ] as const
    }
    // We explicitly don't include routeNames, route.params etc. in the dep list
    // This is to avoid re-initializing the state when these values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState, router, isStateValid])

  const previousRouteKeyListRef = React.useRef(routeKeyList)

  React.useEffect(() => {
    previousRouteKeyListRef.current = routeKeyList
  })

  const previousRouteKeyList = previousRouteKeyListRef.current

  let state = isStateInitialized(currentState)
    ? currentState
    : (initializedState as NavigationState)

  let nextState: NavigationState = state

  if (
    !isArrayEqual(state.routeNames, routeNames) ||
    !isRecordEqual(routeKeyList, previousRouteKeyList)
  ) {
    // When the list of route names change, the router should handle it to remove invalid routes
    nextState = router.getStateForRouteNamesChange(state, {
      routeNames,
      routeParamList,
      routeGetIdList,
      routeKeyChanges: Object.keys(routeKeyList).filter(
        name =>
          previousRouteKeyList.hasOwnProperty(name) &&
          routeKeyList[name] !== previousRouteKeyList[name]
      )
    })
  }

  const previousNestedParamsRef = React.useRef(route?.params)

  React.useEffect(() => {
    previousNestedParamsRef.current = route?.params
  }, [route?.params])

  if (route?.params) {
    const previousParams = previousNestedParamsRef.current

    let action: Action | undefined

    if (
      typeof route.params.state === 'object' &&
      route.params.state != null &&
      route.params !== previousParams
    ) {
      // If the route was updated with new state, we should reset to it
      action = CommonActions.reset(route.params.state)
    } else if (
      typeof route.params.screen === 'string' &&
      ((route.params.initial === false && isFirstStateInitialization) ||
        route.params !== previousParams)
    ) {
      // If the route was updated with new screen name and/or params, we should navigate there
      action = CommonActions.navigate({
        name: route.params.screen,
        params: route.params.params,
        path: route.params.path
      })
    }

    // The update should be limited to current navigator only, so we call the router manually
    const updatedState = action
      ? router.getStateForAction(nextState, action, {
          routeNames,
          routeParamList,
          routeGetIdList
        })
      : null

    nextState =
      updatedState !== null
        ? router.getRehydratedState(updatedState, {
            routeNames,
            routeParamList,
            routeGetIdList
          })
        : nextState
  }

  const shouldUpdate = state !== nextState

  useScheduleUpdate(() => {
    if (shouldUpdate) {
      // If the state needs to be updated, we'll schedule an update
      setState(nextState)
    }
  })

  // The up-to-date state will come in next render, but we don't need to wait for it
  // We can't use the outdated state since the screens have changed, which will cause error due to mismatched config
  // So we override the state object we return to use the latest state as well as notify the parent navigator
  state = nextState

  React.useEffect(() => {
    setKey(navigatorKey)

    if (!getIsInitial()) {
      // If it's not initial render, we need to update the state
      // This will make sure that our container gets notifier of state changes due to new mounts
      // This is necessary for proper screen tracking, URL updates etc.
      setState(nextState)
    }

    return () => {
      // We need to clean up state for this navigator on unmount
      // We do it in a timeout because we need to detect if another navigator mounted in the meantime
      // For example, if another navigator has started rendering, we should skip cleanup
      // Otherwise, our cleanup step will cleanup state for the other navigator and re-initialize it
      setTimeout(() => {
        if (getCurrentState() !== undefined && getKey() === navigatorKey) {
          cleanUpState()
        }
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // We initialize this ref here to avoid a new getState getting initialized
  // whenever initializedState changes. We want getState to have access to the
  // latest initializedState, but don't need it to change when that happens
  const initializedStateRef = React.useRef<NavigationState>()
  initializedStateRef.current = initializedState

  const getState = React.useCallback((): NavigationState => {
    const currentState = getCurrentState()

    return isStateInitialized(currentState)
      ? currentState
      : (initializedStateRef.current as NavigationState)
  }, [getCurrentState, isStateInitialized])

  const emitter = useEventEmitter()

  useFocusEvents({ state, emitter })

  const { listeners: childListeners, addListener } = useChildListeners()
  const { keyedListeners, addKeyedListener } = useKeyedChildListeners()

  const onAction = useOnAction({
    router,
    getState,
    setState,
    key: route?.key,
    actionListeners: childListeners.action,
    beforeRemoveListeners: keyedListeners.beforeRemove,
    routerConfigOptions: { routeNames, routeParamList, routeGetIdList },
    emitter
  })

  const onRouteFocus = useOnRouteFocus({
    router,
    key: route?.key,
    getState,
    setState
  })

  const navigation = useNavigationHelpers({
    id: options.id,
    onAction,
    getState,
    emitter,
    router
  })

  useFocusedListenersChildrenAdapter({
    navigation,
    focusedListeners: childListeners.focus
  })

  useOnGetState({ getState, getStateListeners: keyedListeners.getState })

  const descriptors = useDescriptors({
    state,
    screens,
    navigation,
    screenOptions: options.screenOptions,
    defaultScreenOptions: options.defaultScreenOptions,
    onAction,
    getState,
    setState,
    onRouteFocus,
    addListener,
    addKeyedListener,
    router,
    emitter
  })

  return { state, navigation, descriptors }
}

function useScheduleUpdate(callback: () => void) {
  const { scheduleUpdate, flushUpdates } = React.useContext(
    ScheduleUpdateContext
  )

  scheduleUpdate(callback)

  React.useEffect(flushUpdates)
}

function useFocusEvents({
  state,
  emitter
}: {
  state: NavigationState
  emitter: NavigationEventEmitter
}) {
  const navigation = React.useContext(NavigationContext)
  const lastFocusedKeyRef = React.useRef<string | undefined>()

  const currentFocusedKey = state.routes[state.index].key

  // When the parent screen changes its focus state, we also need to change child's focus
  // Coz the child screen can't be focused if the parent screen is out of focus
  React.useEffect(
    () =>
      navigation?.addListener('focus', () => {
        lastFocusedKeyRef.current = currentFocusedKey
        emitter.emit({ type: 'focus', target: currentFocusedKey })
      }),
    [currentFocusedKey, emitter, navigation]
  )

  React.useEffect(
    () =>
      navigation?.addListener('blur', () => {
        lastFocusedKeyRef.current = undefined
        emitter.emit({ type: 'blur', target: currentFocusedKey })
      }),
    [currentFocusedKey, emitter, navigation]
  )

  React.useEffect(() => {
    const lastFocusedKey = lastFocusedKeyRef.current

    lastFocusedKeyRef.current = currentFocusedKey

    // We wouldn't have `lastFocusedKey` on initial mount
    // Fire focus event for the current route on mount if there's no parent navigator
    if (lastFocusedKey === undefined && !navigation) {
      emitter.emit({ type: 'focus', target: currentFocusedKey })
    }

    // We should only emit events when the focused key changed and navigator is focused
    // When navigator is not focused, screens inside shouldn't receive focused status either
    if (
      lastFocusedKey === currentFocusedKey ||
      !(navigation ? navigation.isFocused() : true)
    ) {
      return
    }

    if (lastFocusedKey === undefined) {
      // Only fire events after initial mount
      return
    }

    emitter.emit({ type: 'blur', target: lastFocusedKey })
    emitter.emit({ type: 'focus', target: currentFocusedKey })
  }, [currentFocusedKey, emitter, navigation])
}

function useOnAction({
  router,
  getState,
  setState,
  key,
  actionListeners,
  beforeRemoveListeners,
  routerConfigOptions,
  emitter
}: {
  router: Router
  getState: () => NavigationState
  setState: (state: AnyState | undefined) => void
  key?: string
  actionListeners: ChildActionListener[]
  beforeRemoveListeners: Record<
    string,
    ((action: Action) => boolean) | undefined
  >
  routerConfigOptions: RouterConfigOptions
  emitter: NavigationEventEmitter
}) {
  const {
    onAction: onActionParent,
    onRouteFocus: onRouteFocusParent,
    addListener: addListenerParent,
    addKeyedListener
  } = React.useContext(NavigationBuilderContext)

  const routerConfigOptionsRef = React.useRef<RouterConfigOptions>(
    routerConfigOptions
  )

  React.useEffect(() => {
    routerConfigOptionsRef.current = routerConfigOptions
  })

  const onAction = React.useCallback(
    (
      action: Action,
      visitedNavigators: Set<string> = new Set<string>()
    ): boolean => {
      const state = getState()

      // Since actions can bubble both up and down, they could come to the same navigator again
      // We keep track of navigators which have already tried to handle the action and return if it's already visited
      if (visitedNavigators.has(state.key)) {
        return false
      }

      visitedNavigators.add(state.key)

      if (typeof action.target !== 'string' || action.target === state.key) {
        let result = router.getStateForAction(
          state,
          action,
          routerConfigOptionsRef.current
        )

        // If a target is specified and set to current navigator, the action shouldn't bubble
        // So instead of `null`, we use the state object for such cases to signal that action was handled
        result = result === null && action.target === state.key ? state : result

        if (result !== null) {
          if (state !== result) {
            const isPrevented = shouldPreventRemove(
              emitter,
              beforeRemoveListeners,
              state.routes,
              result.routes as { key?: string }[],
              action
            )

            if (isPrevented) {
              return true
            }

            setState(result)
          }

          if (onRouteFocusParent !== undefined) {
            // Some actions such as `NAVIGATE` also want to bring the navigated route to focus in the whole tree
            // This means we need to focus all of the parent navigators of this navigator as well
            const shouldFocus = router.shouldActionChangeFocus(action)

            if (shouldFocus && key !== undefined) {
              onRouteFocusParent(key)
            }
          }

          return true
        }
      }

      if (onActionParent !== undefined) {
        // Bubble action to the parent if the current navigator didn't handle it
        if (onActionParent(action, visitedNavigators)) {
          return true
        }
      }

      // If the action wasn't handled by current navigator or a parent navigator, let children handle it
      for (let i = actionListeners.length - 1; i >= 0; i--) {
        const listener = actionListeners[i]

        if (listener(action, visitedNavigators)) {
          return true
        }
      }

      return false
    },
    [
      actionListeners,
      beforeRemoveListeners,
      emitter,
      getState,
      key,
      onActionParent,
      onRouteFocusParent,
      router,
      setState
    ]
  )

  // useOnPreventRemove: let the parent ask this navigator's screens before its route is removed
  const parentRoute = React.useContext(NavigationRouteContext)
  const routeKey = parentRoute?.key

  React.useEffect(() => {
    if (routeKey) {
      return addKeyedListener?.('beforeRemove', routeKey, (action: Action) => {
        const state = getState()

        return shouldPreventRemove(
          emitter,
          beforeRemoveListeners,
          state.routes,
          [],
          action
        )
      })
    }
    return undefined
  }, [addKeyedListener, beforeRemoveListeners, emitter, getState, routeKey])

  React.useEffect(() => addListenerParent?.('action', onAction), [
    addListenerParent,
    onAction
  ])

  return onAction
}

function useOnRouteFocus({
  router,
  getState,
  key: sourceRouteKey,
  setState
}: {
  router: Router
  getState: () => NavigationState
  key?: string
  setState: (state: AnyState | undefined) => void
}) {
  const { onRouteFocus: onRouteFocusParent } = React.useContext(
    NavigationBuilderContext
  )

  return React.useCallback(
    (key: string) => {
      const state = getState()
      const result = router.getStateForRouteFocus(state, key)

      if (result !== state) {
        setState(result)
      }

      if (onRouteFocusParent !== undefined && sourceRouteKey !== undefined) {
        onRouteFocusParent(sourceRouteKey)
      }
    },
    [getState, onRouteFocusParent, router, setState, sourceRouteKey]
  )
}

function useNavigationHelpers({
  id: navigatorId,
  onAction,
  getState,
  emitter,
  router
}: {
  id?: string
  onAction: (action: Action) => boolean
  getState: () => NavigationState
  emitter: NavigationEventEmitter
  router: Router
}): NavigationHelpers {
  const { onUnhandledAction } = React.useContext(NavigationBuilderContext)
  const parentNavigationHelpers = React.useContext(NavigationContext)

  return React.useMemo(() => {
    const dispatch = (op: Action | ((state: NavigationState) => Action)) => {
      const action = typeof op === 'function' ? op(getState()) : op

      const handled = onAction(action)

      if (!handled) {
        onUnhandledAction?.(action)
      }
    }

    const actions: Record<string, (...args: any[]) => Action> = {
      ...router.actionCreators,
      ...CommonActions
    }

    const helpers = Object.keys(actions).reduce<
      Record<string, (...args: any[]) => void>
    >((acc, name) => {
      acc[name] = (...args: any[]) => dispatch(actions[name](...args))
      return acc
    }, {})

    const navigationHelpers: NavigationHelpers = {
      ...parentNavigationHelpers,
      ...helpers,
      dispatch,
      emit: emitter.emit,
      isFocused: parentNavigationHelpers
        ? parentNavigationHelpers.isFocused
        : () => true,
      canGoBack: () => {
        const state = getState()

        return (
          router.getStateForAction(state, CommonActions.goBack(), {
            routeNames: state.routeNames,
            routeParamList: {},
            routeGetIdList: {}
          }) !== null ||
          parentNavigationHelpers?.canGoBack() ||
          false
        )
      },
      getId: () => navigatorId,
      getParent: (id?: string) => {
        if (id !== undefined) {
          let current: any = navigationHelpers

          while (current && id !== current.getId()) {
            current = current.getParent()
          }

          return current
        }

        return parentNavigationHelpers
      },
      getState
    }

    return navigationHelpers
  }, [
    navigatorId,
    emitter.emit,
    getState,
    onAction,
    onUnhandledAction,
    parentNavigationHelpers,
    router
  ])
}

function useFocusedListenersChildrenAdapter({
  navigation,
  focusedListeners
}: {
  navigation: NavigationHelpers
  focusedListeners: FocusedNavigationListener[]
}) {
  const { addListener } = React.useContext(NavigationBuilderContext)

  const listener = React.useCallback(
    (callback: FocusedNavigationCallback<any>) => {
      if (navigation.isFocused()) {
        for (const listener of focusedListeners) {
          const { handled, result } = listener(callback)

          if (handled) {
            return { handled, result }
          }
        }

        return { handled: true, result: callback(navigation) }
      } else {
        return { handled: false, result: null }
      }
    },
    [focusedListeners, navigation]
  ) as FocusedNavigationListener

  React.useEffect(() => addListener?.('focus', listener), [
    addListener,
    listener
  ])
}

function useOnGetState({
  getState,
  getStateListeners
}: {
  getState: () => NavigationState
  getStateListeners: Record<string, (() => AnyState | undefined) | undefined>
}) {
  const { addKeyedListener } = React.useContext(NavigationBuilderContext)
  const route = React.useContext(NavigationRouteContext)
  const key = route ? route.key : 'root'

  const getRehydratedState = React.useCallback(() => {
    const state = getState()

    // Avoid returning new route objects if we don't need to
    const routes = state.routes.map(route => {
      const childState = getStateListeners[route.key]?.()

      if (route.state === childState) {
        return route
      }

      return { ...route, state: childState }
    })

    if (isArrayEqual(state.routes, routes)) {
      return state
    }

    return { ...state, routes }
  }, [getState, getStateListeners])

  React.useEffect(
    () => addKeyedListener?.('getState', key, getRehydratedState),
    [addKeyedListener, getRehydratedState, key]
  )
}

// ---------------------------------------------------------------------------
// Descriptors, per-route navigation objects and scenes
// ---------------------------------------------------------------------------

function useNavigationCache({
  state,
  getState,
  navigation,
  setOptions,
  router,
  emitter
}: {
  state: NavigationState
  getState: () => NavigationState
  navigation: NavigationHelpers
  setOptions: (
    cb: (options: Record<string, object>) => Record<string, object>
  ) => void
  router: Router
  emitter: NavigationEventEmitter
}): Record<string, NavigationProp> {
  // Cache object which holds navigation objects for each screen
  // We use `React.useMemo` instead of `React.useRef` coz we want to invalidate it when deps change
  // In reality, these deps will rarely change, if ever
  const cache = React.useMemo(
    () => ({ current: {} as Record<string, NavigationProp> }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getState, navigation, setOptions, router, emitter]
  )

  const actions: Record<string, (...args: any[]) => Action> = {
    ...router.actionCreators,
    ...CommonActions
  }

  cache.current = state.routes.reduce<Record<string, NavigationProp>>(
    (acc, route) => {
      const previous = cache.current[route.key]

      if (previous) {
        // If a cached navigation object already exists, reuse it
        // This is required to avoid unnecessary re-renders of the screens
        acc[route.key] = previous
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { _, ...rest } = navigation

        const dispatch = (
          thunk:
            | Action
            | ((state: NavigationState) => Action | null | undefined)
        ) => {
          const action = typeof thunk === 'function' ? thunk(getState()) : thunk

          if (action != null) {
            navigation.dispatch({ source: route.key, ...action })
          }
        }

        const helpers = Object.keys(actions).reduce<
          Record<string, (...args: any[]) => void>
        >((acc, name) => {
          acc[name] = (...args: any[]) => dispatch(actions[name](...args))
          return acc
        }, {})

        acc[route.key] = {
          ...rest,
          ...helpers,
          ...emitter.create(route.key),
          dispatch: (thunk: any) => dispatch(thunk),
          getParent: (id?: string) => {
            if (id !== undefined && id === rest.getId()) {
              return acc[route.key]
            }

            return rest.getParent(id)
          },
          setOptions: (options: object) =>
            setOptions(o => ({
              ...o,
              [route.key]: { ...o[route.key], ...options }
            })),
          isFocused: () => {
            const state = getState()

            if (state.routes[state.index].key !== route.key) {
              return false
            }

            // If the current screen is focused, we also need to check if parent navigator is focused
            // This makes sure that we return the focus state in the whole tree, not just this navigator
            return navigation ? navigation.isFocused() : true
          }
        } as NavigationProp
      }

      return acc
    },
    {}
  )

  return cache.current
}

function SceneView({
  screen,
  route,
  navigation,
  routeState,
  getState,
  setState
}: {
  screen: ScreenProps
  route: Route
  navigation: NavigationProp
  routeState: AnyState | undefined
  getState: () => NavigationState
  setState: (state: AnyState | undefined) => void
}) {
  const navigatorKeyRef = React.useRef<string | undefined>()
  const getKey = React.useCallback(() => navigatorKeyRef.current, [])

  const setKey = React.useCallback((key: string) => {
    navigatorKeyRef.current = key
  }, [])

  const getCurrentState = React.useCallback(() => {
    const state = getState()
    const currentRoute = state.routes.find(r => r.key === route.key)

    return currentRoute ? currentRoute.state : undefined
  }, [getState, route.key])

  const setCurrentState = React.useCallback(
    (child: AnyState | undefined) => {
      const state = getState()

      setState({
        ...state,
        routes: state.routes.map(r =>
          r.key === route.key ? { ...r, state: child } : r
        )
      })
    },
    [getState, route.key, setState]
  )

  const isInitialRef = React.useRef(true)

  React.useEffect(() => {
    isInitialRef.current = false
  })

  const getIsInitial = React.useCallback(() => isInitialRef.current, [])

  const context = React.useMemo(
    () => ({
      state: routeState,
      getState: getCurrentState,
      setState: setCurrentState,
      getKey,
      setKey,
      getIsInitial
    }),
    [routeState, getCurrentState, setCurrentState, getKey, setKey, getIsInitial]
  )

  const ScreenComponent = screen.component

  return (
    <NavigationStateContext.Provider value={context}>
      <StaticContainer
        name={screen.name}
        render={ScreenComponent || screen.children}
        navigation={navigation}
        route={route}
      >
        {ScreenComponent !== undefined ? (
          <ScreenComponent navigation={navigation} route={route} />
        ) : screen.children !== undefined ? (
          screen.children({ navigation, route })
        ) : null}
      </StaticContainer>
    </NavigationStateContext.Provider>
  )
}

function useDescriptors({
  state,
  screens,
  navigation,
  screenOptions,
  defaultScreenOptions,
  onAction,
  getState,
  setState,
  onRouteFocus,
  addListener,
  addKeyedListener,
  router,
  emitter
}: {
  state: NavigationState
  screens: Record<string, ScreenConfigWithParent>
  navigation: NavigationHelpers
  screenOptions?: NavigatorOptions['screenOptions']
  defaultScreenOptions?: object
  onAction: (action: Action, visitedNavigators?: Set<string>) => boolean
  getState: () => NavigationState
  setState: (state: AnyState | undefined) => void
  onRouteFocus: (key: string) => void
  addListener: NavigationBuilderContextValue['addListener']
  addKeyedListener: NavigationBuilderContextValue['addKeyedListener']
  router: Router
  emitter: NavigationEventEmitter
}): Record<string, Descriptor> {
  const [options, setOptions] = React.useState<Record<string, object>>({})
  const { onUnhandledAction } = React.useContext(NavigationBuilderContext)

  const context = React.useMemo(
    () => ({
      navigation,
      onAction,
      addListener,
      addKeyedListener,
      onRouteFocus,
      onUnhandledAction
    }),
    [
      navigation,
      onAction,
      addListener,
      addKeyedListener,
      onRouteFocus,
      onUnhandledAction
    ]
  )

  const navigations = useNavigationCache({
    state,
    getState,
    navigation,
    setOptions,
    router,
    emitter
  })

  return state.routes.reduce<Record<string, Descriptor>>((acc, route) => {
    const config = screens[route.name]
    const screen = config.props
    const routeNavigation = navigations[route.key]

    const optionsList = [
      // The default `screenOptions` passed to the navigator
      screenOptions,
      // The `screenOptions` props passed to `Group` elements
      ...((config.options ? config.options.filter(Boolean) : []) as any[]),
      // The `options` prop passed to `Screen` elements,
      screen.options,
      // The options set via `navigation.setOptions`
      options[route.key]
    ]

    const customOptions = optionsList.reduce(
      (acc, curr: any) =>
        Object.assign(
          acc,
          typeof curr !== 'function'
            ? curr
            : curr({ route, navigation: routeNavigation })
        ),
      {}
    )

    const mergedOptions = { ...defaultScreenOptions, ...customOptions }

    acc[route.key] = {
      route,
      navigation: routeNavigation,
      render() {
        return (
          <NavigationBuilderContext.Provider key={route.key} value={context}>
            <NavigationContext.Provider value={routeNavigation}>
              <NavigationRouteContext.Provider value={route}>
                <SceneView
                  navigation={routeNavigation}
                  route={route}
                  screen={screen}
                  routeState={
                    state.routes.find(r => r.key === route.key)?.state
                  }
                  getState={getState}
                  setState={setState}
                />
              </NavigationRouteContext.Provider>
            </NavigationContext.Provider>
          </NavigationBuilderContext.Provider>
        )
      },
      options: mergedOptions
    }

    return acc
  }, {})
}
